import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  Grid,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  getDemoBotDataAction,
  updateDemoBotAction,
} from "../ProjectSection/Agents/Store/Agent.action";
import {
  extractContactFields,
  LoadingSlides,
} from "../CommonComponents/Common";
import { getWorkflowsAction } from "../Actions/CommonActions";
import CommonPopup from "../CommonComponents/PopUpCards/CommonPopup";
import { ReactComponent as SuccessIcon } from "../../../assets/Images/SuccessProjectIcon.svg";
import LinkBody from "./LinkBody";

interface DemoBot {
  _id: string;
  brandName: string;
  link: string;
  workflows?: string;
}

const LinksList = () => {
  const reducer: any = useSelector((state) => state);
  const {
    ProjectReducer: { projectList },
  } = reducer;

  const dispatch = useDispatch();

  const [botList, setBotList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [spin, setSpin] = useState(false);
  const [modal, setModal] = useState(false);
  const [workflowList, setWorkflowList] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<string>("");
  const [workflowParams, setWorkflowParams] = useState<string[]>([]);
  const [successPopup, setSuccessPopup] = useState(false);
  const [selectedBot, setSelectedBot] = useState<DemoBot | null>(null);

  const updateParamsForSelection = (workflowId: string) => {
    const wfObj: any = workflowList?.find((w: any) => w.id === workflowId);
    if (wfObj?.workflow) {
      const params = extractContactFields(wfObj?.workflow);
      setWorkflowParams(params);
    } else {
      setWorkflowParams([]);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const params = { countData: false };
      const res: any = await dispatch(getDemoBotDataAction(params));

      const workflowres: any = await dispatch(getWorkflowsAction());
      const workflowOnly = workflowres?.filter((w) => w?.type === "workflow");

      const workflowLists = workflowOnly?.map((wf) => ({
        id: wf._id,
        name: wf?.workflows?.name,
        workflow: { ...wf?.workflows, id: wf?._id },
      }));

      setWorkflowList(workflowLists);
      setBotList(res);
      setLoading(false);
    };
    init();
  }, [dispatch]);

  const projectName = (item) => {
    const project = projectList?.find(
      (project) => project?._id === item?.projectId
    );
    return project?.projectName;
  };

  const handleAddWorkflow = async () => {
    if (!selectedWorkflow) return;
    setSpin(true);

    const obj = {
      ...selectedBot,
      workflowId: selectedWorkflow,
      workflowParams,
    };

    const demoBotParam = { demobotDataId: selectedBot?._id };

    const updateBotRes: any = await dispatch(
      updateDemoBotAction(obj, demoBotParam)
    );

    if (updateBotRes === 200) {
      const res: any = await dispatch(
        getDemoBotDataAction({ countData: false })
      );
      setBotList(res);
      setModal(false);
      setSelectedWorkflow("");
    }

    setSpin(false);
    setSuccessPopup(true);
  };

  const linkBodyObj = {
    projectName,
    selectedBot,
    workflowList,
    selectedWorkflow,
    setSelectedWorkflow,
    handleAddWorkflow,
    spin,
    setModal,
    updateParamsForSelection,
  };

  return (
    <>
      <Box py={1}>
        <Typography className="font-20-grey">Links</Typography>

        <Grid mt={2} container spacing={3}>
          {loading ? (
            <Box className="flex-xy-center" width="100%" height="70vh">
              <LoadingSlides bgColor="#FF6A00" />
            </Box>
          ) : botList?.length > 0 ? (
            botList?.map((item: any, idx) => (
              <Grid key={idx} size={{ xs: 12, md: 4 }}>
                <Card
                  variant="outlined"
                  sx={{
                    height: "100%",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "#1976d2",
                      boxShadow: "0 2px 8px rgba(25, 118, 210, 0.08)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                    <Typography className="font-14-grey" mb={1}>
                      Project Name - <strong>{projectName(item)}</strong>
                    </Typography>

                    <Typography className="font-14-grey" mb={1}>
                      Demo Bot Url -{" "}
                      <strong>{`${item?.link}?aid=${item?._id}&w=true`}</strong>
                    </Typography>

                    <Typography className="font-14-600-grey" mb={0.5}>
                      Linked Workflow:
                    </Typography>

                    <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                      {item?.workflow ? (
                        workflowList
                          ?.filter((wf: any) => wf?.id === item?.workflow)
                          ?.map((wf: any) => (
                            <Box
                              key={wf?.id}
                              px={1.5}
                              py={0.5}
                              border="1px solid #ccc"
                              borderRadius={2}
                              fontSize="0.8rem"
                              color="text.primary"
                            >
                              {wf?.name}
                            </Box>
                          ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No workflow linked
                        </Typography>
                      )}
                    </Box>

                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      sx={{
                        textTransform: "none",
                        fontWeight: 500,
                        borderRadius: 1.5,
                        py: 0.75,
                        mt: 1,
                        "&:hover": {
                          backgroundColor: "primary.50",
                        },
                      }}
                      onClick={() => {
                        setModal(true);
                        setSelectedBot(item);
                        setSelectedWorkflow(item?.workflow || "");
                      }}
                    >
                      Add Workflow
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid size={12}>
              <Typography color="text.secondary" align="center">
                No Data Found
              </Typography>
            </Grid>
          )}
        </Grid>

        <Dialog
          open={modal}
          onClose={() => {
            setModal(false);
            setSelectedWorkflow("");
          }}
          fullWidth
          maxWidth="sm"
        >
          <LinkBody obj={linkBodyObj} />
        </Dialog>

        <CommonPopup
          message="Workflow Linked Successfully"
          show={successPopup}
          toggle={() => setSuccessPopup((prev) => !prev)}
          Icon={SuccessIcon}
          heading="Workflow Linked"
        />
      </Box>
    </>
  );
};

export default LinksList;
